import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';
import { migrateProject, type NumberNodeProject } from '@numbernode/contracts';

export type ProjectFile = { key: string; bytes: Uint8Array };

async function sha256(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
}

export async function packProject(project: NumberNodeProject, files: ProjectFile[]): Promise<Uint8Array> {
  const projectBytes = strToU8(JSON.stringify(project, null, 2));
  const entries: Record<string, Uint8Array> = { 'project.json': projectBytes };
  const hashes: Record<string, string> = { 'project.json': await sha256(projectBytes) };
  for (const file of files) {
    const key = file.key.startsWith('data/') ? file.key : `data/${file.key}`;
    entries[key] = file.bytes;
    hashes[key] = await sha256(file.bytes);
  }
  entries['manifest.json'] = strToU8(JSON.stringify({
    format: 'numbernode-project',
    formatVersion: project.formatVersion,
    projectId: project.id,
    name: project.name,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    hashes,
  }, null, 2));
  return zipSync(entries, { level: 6 });
}

export async function unpackProject(bytes: Uint8Array): Promise<{ project: NumberNodeProject; files: ProjectFile[] }> {
  const entries = unzipSync(bytes);
  const projectBytes = entries['project.json'];
  if (!projectBytes) throw new Error('This archive does not contain project.json.');
  const project = migrateProject(JSON.parse(strFromU8(projectBytes)));
  const files = Object.entries(entries)
    .filter(([key]) => key.startsWith('data/'))
    .map(([key, data]) => ({ key, bytes: data }));
  return { project, files };
}

export function staticViewerHtml(project: NumberNodeProject): string {
  const safe = JSON.stringify(project).replaceAll('<', '\\u003c');
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><title>${project.name.replaceAll('<','&lt;')}</title><style>body{font-family:system-ui;margin:0;background:#f5f6f8;color:#16202a}main{max-width:1100px;margin:auto;padding:40px}.card{background:white;border:1px solid #dfe3e8;border-radius:14px;padding:24px;margin:16px 0}pre{white-space:pre-wrap}</style></head><body><main><h1>${project.name.replaceAll('<','&lt;')}</h1><p>Portable NumberNode project summary. Open the .numbernode file in NumberNode Studio for fully interactive local analysis.</p><div class="card"><h2>Sources</h2><pre id="sources"></pre></div><div class="card"><h2>Visuals</h2><pre id="visuals"></pre></div></main><script>const p=${safe};document.querySelector('#sources').textContent=JSON.stringify(p.sources,null,2);document.querySelector('#visuals').textContent=JSON.stringify(p.visuals,null,2);</script></body></html>`;
}
